import Rollbar from '../../../utils/rollbar'
import { SupplierBase } from '../json-schemas/base/supplier'
import { MarketplaceBase } from '../json-schemas/base/marketplace'
import { httpGetQueryParams } from './http.service'
import { toUpperCaseFirst } from './lodash.service'
import { CityBase } from '../json-schemas/base/city'
import { websiteCastingWidgetCity } from '../models/website-casting-widgets.service'

interface ResolverResult {
  status: 'results' | 'loading' | 'no-results' |  'error' | 'invalid'
  data: {
    id: string
    city: string
    lat: number
    lng: number
    postalCode: string
    country: string
  }[]
}
interface MapsGeocodeResults {
  results: {
    geometry: { location: { lat: () => number, lng: () => number } },
    address_components: google.maps.GeocoderAddressComponent[]
    postcode_localities?: string[]
  }[]
}

interface Geocoder {
  geocode: (query: google.maps.GeocoderRequest) => Promise<MapsGeocodeResults>
}

export async function castingGeodingSendQuery (cleanedValue: string, market: Pick<SupplierBase | MarketplaceBase, 'id' | 'market_type' | 'coverage' | 'country'>, country: string | null = null): Promise<ResolverResult> {
  if (!cleanedValue.length) return { status: 'invalid', data: [] }

  try {
    const results = await getGeocoderInstance(market)
      .geocode(getGeocoderRequest(cleanedValue, market, country))
      .then(geoResults => formatGeoResults(geoResults))
      .then(geoResults => geoResults.filter(locality => locality.city))

    if (results.length === 0) throw { code: 'ZERO_RESULTS' }
    return { status: 'results', data: results }
  } catch (err: any) {
    if (err instanceof Error) {
      err.message += `(input was '${cleanedValue}')`
      Rollbar.error(err)
    } else if (err.status !== 'no-results') {
      Rollbar.debug(new Error(JSON.stringify(err) + `(input was '${cleanedValue}')`))
    }

    if (['FR', 'GP', 'MQ', 'RE'].includes(market.country) && /\d/.test(cleanedValue)) { // looks like a postal code
      const fallback = await getFrenchLocalityFromPostalCodeUsingGouvApi(cleanedValue)
      if (fallback.length) return { status: 'results', data: fallback }
    }

    if (err.code === 'ZERO_RESULTS') return { status: 'no-results', data: [] }
    return { status: 'error', data: [] }
  }
}

function getGeocoderInstance (market: Pick<SupplierBase | MarketplaceBase, 'id' | 'market_type' | 'coverage' | 'country'>): Geocoder {
  if (window.env === 'test') { // returns a mock geocoder resolving only some hand crafted responses
    return {
      geocode (query) {
        const payloadParis = {
          address_components: [
            { long_name: "Paris", short_name: "Paris", types: ["locality","political"] },
            { long_name: "Arrondissement de Paris", short_name: "Arrondissement de Paris", types: ["administrative_area_level_2","political"] },
            { long_name: "Île-de-France", short_name: "IDF", types: ["administrative_area_level_1","political"] },
            { long_name: "France", short_name: "FR", types: ["country","political"] }
          ],
          geometry: { location: { lat () { return 48.856614 }, lng () { return 2.3522219 } } }
        }

        if (query.address.startsWith('MOCK-TEST-FR')) return Promise.resolve({ results: [payloadParis] })
        else throw new Error('no mocking fallback')
      }
    }
  } else {
    return {
      async geocode (query) {
        // by using query.componentRestrictions?.country, we reflect the picked country in case market.coverage == worldwide
        const params = { q: query.address, country: query.componentRestrictions?.country, component_restrictions: query.componentRestrictions }
        const res: CityBase[] = await websiteCastingWidgetCity(market, params)
        if (res.length === 0) throw { code: 'ZERO_RESULTS' }

        return {
          results: res.map(city => ({
            address_components: [
              { long_name: city.name, short_name: city.name, types: ['locality'] },
              { long_name: city.zipcode, short_name: city.zipcode, types: ['postal_code'] }
            ],
            geometry: { location: { lat () { return city.latitude }, lng () { return city.longitude } } }
          }))
        }
      }
    }
  }
}

function getGeocoderRequest (address: string, market: Pick<SupplierBase | MarketplaceBase, 'coverage' | 'country'>, country: string | null) {
  const prms: google.maps.GeocoderRequest = { address }
  if (market.coverage != 'worldwide' || country) prms.componentRestrictions = { country: country || market.country }
  return prms
}

function formatGeoResults (geoResults: MapsGeocodeResults) {
  return geoResults.results.flatMap(geoResult => (geoResult.postcode_localities ? formatPostcodeLocalities : formatGeoObject)(geoResult))
}

function formatPostcodeLocalities (geoResult: MapsGeocodeResults['results'][0]) {
  const postcodeLocalities = geoResult.postcode_localities
  const addressComponents = geoResult.address_components

  return postcodeLocalities.map(postcodeLocality => ({
    id: crypto?.randomUUID?.() || Date.now().toString(),
    city: postcodeLocality,
    postalCode: getInfoFromAddressComponents(addressComponents, 'postal_code')?.long_name,
    country: getInfoFromAddressComponents(addressComponents, 'country')?.short_name,
    lat: geoResult.geometry?.location?.lat(),
    lng: geoResult.geometry?.location?.lng()
  }))
}

function formatGeoObject (geoResult: MapsGeocodeResults['results'][0]) {
  return {
    id: crypto?.randomUUID?.() || Date.now().toString(),
    city: getNameFromAddressComponents(geoResult)?.long_name,
    postalCode: getInfoFromAddressComponents(geoResult.address_components, 'postal_code')?.long_name,
    country: getInfoFromAddressComponents(geoResult.address_components, 'country')?.short_name,
    lat: geoResult.geometry?.location?.lat(),
    lng: geoResult.geometry?.location?.lng()
  }
}

function getInfoFromAddressComponents (addressComponents: google.maps.GeocoderAddressComponent[], infoType: string) {
  return addressComponents.find(addressComponent => addressComponent.types.indexOf(infoType) > -1)
}

function getNameFromAddressComponents (geoResult: MapsGeocodeResults['results'][0]) {
  const name = getInfoFromAddressComponents(geoResult.address_components, 'locality') ||
    getInfoFromAddressComponents(geoResult.address_components, 'colloquial_area') ||
    getInfoFromAddressComponents(geoResult.address_components, 'postal_town') ||
    getInfoFromAddressComponents(geoResult.address_components, 'postal_code')

  if (!name) Rollbar.error(new Error('GoogleMap geocoding API issue, can\'t get city name for geoResult'), geoResult)
  return name
}

/**
 * Some postal codes in France > DOM-TOM are badly resolved by the Google Maps API.
 * If a postal is unknown, we fallback to that API
 */
async function getFrenchLocalityFromPostalCodeUsingGouvApi (postalCode: string): Promise<ResolverResult['data']> {
  const params = httpGetQueryParams({ dataset: 'code-postal-code-insee-2015', q: `code_postal=${postalCode}` })
  const res = await fetch('https://public.opendatasoft.com/api/records/1.0/search/' + params)
  const data: { records: { recordid: string, fields: { nom_com: string, code_postal: string, coordonnees_gps: [number, number], geo_point_2d: [number, number] } }[] } = await res.json()

  return data.records.map(entry => {
    const wellCaseCityName = entry.fields.nom_com.toLocaleLowerCase().split('-').map(toUpperCaseFirst).join('-').split(' ').map(toUpperCaseFirst).join(' ')
    const coordinates = entry.fields.coordonnees_gps || entry.fields.geo_point_2d
    const postalCode = entry.fields.code_postal.split('/')[0] // example Fort-De-France (97200/97234)

    return {
      id: entry.recordid,
      city: wellCaseCityName,
      content: `${wellCaseCityName} (${entry.fields.code_postal})`,
      lat: coordinates[0],
      lng: coordinates[1],
      postalCode, 
      country: postalCode.startsWith('971') ? 'GP' :
        postalCode.startsWith('972') ? 'MQ' :
        postalCode.startsWith('973') ? 'GF' :
        postalCode.startsWith('974') ? 'RE' :
        postalCode.startsWith('975') ? 'PM' :
        postalCode.startsWith('976') ? 'YT' :
        postalCode.startsWith('978') ? 'MF' :
        postalCode.startsWith('986') ? 'WF' :
        postalCode.startsWith('987') ? 'PF' :
        postalCode.startsWith('988') ? 'NC' :
        'FR'
    }
  })
}
